import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Gift, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

// Annual-subscriber guest pass status card for the dashboard.
// Renders nothing unless the user has a pass record (not_issued → hidden).
export const GuestPassCard = () => {
  const [pass, setPass] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/guest-pass/me');
      setPass(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!pass || pass.status === 'not_issued') return null;

  const expiry = pass.expires_at
    ? new Date(pass.expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;
  const daysToExpiry = pass.expires_at
    ? Math.ceil((new Date(pass.expires_at) - new Date()) / (1000 * 60 * 60 * 24))
    : null;
  const expiringSoon = pass.status === 'available' && daysToExpiry != null && daysToExpiry <= 30 && daysToExpiry > 0;

  const handleCancelInvite = async () => {
    if (!window.confirm(`Cancel the invitation to ${pass.guest_first_name}? Your pass returns to your balance.`)) return;
    setCancelling(true);
    try {
      const res = await api.post('/guest-pass/cancel-invite');
      toast.success(res.data.message);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not cancel invitation');
    }
    setCancelling(false);
  };

  const statusContent = {
    available: {
      title: expiringSoon ? `Guest pass expires in ${daysToExpiry} day${daysToExpiry === 1 ? '' : 's'}` : 'Guest pass ready',
      body: expiringSoon
        ? 'Don\'t let it go to waste — bring a friend free to a workshop before it expires. It\'s the nicest introduction to the community.'
        : 'Bring a friend free to one workshop this year. Redeem it when you register for a workshop.',
      accent: expiringSoon ? 'bg-amber-50 border-amber-300' : 'bg-teal/10 border-teal/30',
    },
    pending: {
      title: `Invitation sent to ${pass.guest_first_name || 'your guest'}`,
      body: 'Waiting for them to claim their seat. If unclaimed within 72 hours, the seat is released and your pass returns.',
      accent: 'bg-amber-50 border-amber-200',
    },
    redeemed: {
      title: 'Guest pass redeemed',
      body: `${pass.guest_first_name || 'Your guest'} claimed their seat${pass.event_title ? ` for ${pass.event_title}` : ''}${pass.claimed_at ? ` on ${new Date(pass.claimed_at).toLocaleDateString()}` : ''}.`,
      accent: 'bg-white border-border-subtle',
    },
    expired: {
      title: 'Guest pass expired',
      body: 'Your yearly guest pass expired unused. A new one is issued when your annual subscription renews.',
      accent: 'bg-gray-50 border-gray-200',
    },
    void: {
      title: 'Guest pass no longer active',
      body: 'Your pass was voided when your subscription changed.',
      accent: 'bg-gray-50 border-gray-200',
    },
  }[pass.status] || null;

  if (!statusContent) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl border p-5 ${statusContent.accent}`}
      data-testid="guest-pass-card"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-teal/15 flex items-center justify-center shrink-0">
          <Gift size={18} className="text-teal" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-navy" data-testid="guest-pass-status-title">{statusContent.title}</p>
          <p className="text-sm text-text-mid mt-0.5">{statusContent.body}</p>
          {expiry && pass.status === 'available' && (
            <p className={`text-xs mt-1.5 ${expiringSoon ? 'text-amber-700 font-semibold' : 'text-text-light'}`} data-testid="guest-pass-expiry">
              Valid until {expiry}
            </p>
          )}
          {pass.status === 'pending' && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleCancelInvite}
              disabled={cancelling}
              className="mt-3 rounded-full border-red-300 text-red-600 hover:bg-red-50 text-xs"
              data-testid="guest-pass-cancel-invite-btn"
            >
              {cancelling ? <Loader2 size={14} className="animate-spin mr-1" /> : <X size={14} className="mr-1" />}
              Cancel invitation
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default GuestPassCard;
